import { Component, lazy, Suspense, useMemo, useSyncExternalStore, type ReactNode } from "react";
import { useMatch } from "react-router";
import {
  browserPendingStore,
  ownPending,
  readPendingRaw,
  subscribePending,
} from "../../lib/tts-pending.ts";

/**
 * Привратник баннера «озвучка из заготовки» (`docs/tts-in-plan-design.md`,
 * раздел 5.3).
 *
 * Каркас знает ровно один факт: есть ли незакрытое намерение. Пока его нет, не
 * грузится НИЧЕГО: ни слов озвучки, ни ручек, ни расписания опроса. Первый байт
 * чанка озвучки едет после первого заказа из окна и никогда у ученика, потому
 * что намерение пишет только окно, а окна у ученика нет.
 *
 * ⚠️ **Импортов из `features/tts` здесь нет ни одного, кроме `import()` на
 * баннер.** Этот модуль лежит в стартовом чанке (каркас импортируется
 * статически), и статический импорт втащил бы туда слова, ручки и схемы
 * озвучки. Замок на это стоит в сборке (раздел 11).
 *
 * ⚠️ **В комнате урока и в архиве баннера нет, и держится это МЕСТОМ**, как у
 * полосы версии: привратник стоит внутри `Shell`, а комната, архив и погашение
 * ссылки живут вне каркаса (`App.tsx`).
 */

/** Единственный путь к модулю баннера, и только `import()`: разбор в шапке. */
const loadBar = () => import("../tts/OrderProgressBar.tsx");

const LazyOrderProgressBar = lazy(() => loadBar().then((m) => ({ default: m.OrderProgressBar })));

/**
 * Прогреть чанк баннера заранее: зовёт окно заказа в миг нажатия «Озвучить»
 * (07.10.2026). Иначе при первом заказе в сессии строка «Озвучка запущена»
 * ждала бы после закрытия окна ещё и загрузки чанка, на нашей сети это
 * секунды пустоты. Путь тот же `import()`, статического ребра к баннеру нет.
 * Отказ молчит: привратник попробует сам, когда появится намерение.
 */
export function preloadOrderProgressBar(): void {
  loadBar().catch(() => undefined);
}

const subscribe = (onChange: () => void) =>
  subscribePending(onChange, typeof window === "undefined" ? null : window);

const snapshot = () => readPendingRaw(browserPendingStore());

/**
 * `userId` это владелец записей, которые здесь показываются; `null` не
 * показывает ничего. Хранилище общее у всех, кто входил в этом браузере, а
 * выход его не чистит, поэтому чужая запись не видна и не правится вовсе
 * (шапка `lib/tts-pending.ts`).
 */
export function TtsProgressGate({ userId }: { userId: string | null }) {
  /*
    Снимок это СТРОКА из хранилища, а не разобранный список: строки сравниваются
    по значению, поэтому React перерисовывает только тогда, когда запись
    действительно изменилась. Разбор схемой ниже, и битая запись даёт пустой
    список, а не упавший кабинет.
  */
  const raw = useSyncExternalStore(subscribe, snapshot, () => null);
  const entries = useMemo(() => ownPending(raw, userId, Date.now()), [raw, userId]);
  /*
    Название заготовки в строке зависит от того, в ней ли человек. Считается
    сравнением `planId` маршрута с `planId` записи, тем же `useMatch`, которым
    `Shell` узнаёт редактор, а не фактом «открыт какой-то редактор»: иначе
    открытая чужая заготовка проглотила бы название у нашей строки.
  */
  const openPlanId = useMatch("/plans/:planId")?.params.planId ?? null;

  if (userId === null || entries.length === 0) return null;
  return (
    <SilentBoundary>
      <Suspense fallback={null}>
        <LazyOrderProgressBar userId={userId} entries={entries} openPlanId={openPlanId} />
      </Suspense>
    </SilentBoundary>
  );
}

/**
 * Чанк баннера не загрузился (выкатка сменила имена файлов, оборвалась сеть):
 * баннера нет, а кабинет стоит. Без этой границы отказ `import()` поднялся бы
 * до корня и погасил весь каркас ради одной строки состояния. Полоса «вышла
 * новая версия» в том же каркасе скажет про выкатку сама.
 */
class SilentBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  override state = { failed: false };

  static getDerivedStateFromError(): { failed: boolean } {
    return { failed: true };
  }

  override render() {
    return this.state.failed ? null : this.props.children;
  }
}
